import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as ReactLink } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import {
  Alert,
  AlertDescription,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Link,
  Stack,
  Text,
  Flex,
} from '@chakra-ui/react';

import { extractErrors, isInvalidField } from '@/lib/util';
import { renderErrors } from '@/ui/util';

import { createMePasswordReset } from '@/store/mePasswordReset/actions';

import { useSchemaForCreate } from './schema';

export function PasswordResetCreate() {
  const dispatch = useDispatch();

  const [errors, setErrors] = useState([]);
  const [state, setState] = useState();

  const validationSchema = useSchemaForCreate();

  const onSubmit = useCallback(
    async (values, actions) => {
      try {
        const { email } = values;
        const user = { email };
        const { errors, status } = await dispatch(
          createMePasswordReset({ user })
        ).unwrap();
        // check errors
        if (status && status >= 400) {
          extractErrors(errors, 'user', actions.setFieldError);
        } else {
          setState('create_success');
        }
      } catch (e) {
        console.error(e);
        setErrors(['System error']);
      } finally {
        actions.setSubmitting(false);
      }
    },
    [setState, setErrors, dispatch]
  );

  return (
    <Stack
      spacing={8}
      mx="auto"
      w="lg"
      minH="lg"
      py={8}
      px={8}
      justifyContent="center"
    >
      <Stack alignItems="center">
        <Heading fontSize="4xl" color="secondary-900">
          Forgot password
        </Heading>
      </Stack>
      <Formik
        initialValues={{}}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Stack spacing={8}>
            {errors.map((error, idx) => (
              <Alert key={idx} status="error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ))}
            {formik.errors.base ? (
              <Alert status="error">
                <AlertDescription>
                  {renderErrors('Failed:', formik.errors.base)}
                </AlertDescription>
              </Alert>
            ) : null}
            {state === 'create_success' ? (
              <>
                <Alert
                  status="success"
                  bg="rgba(22, 96, 85, 0.06)"
                  color="brand.100"
                  borderRadius="6px"
                >
                  <AlertDescription fontWeight="semiBold" fontSize="14px">
                    We’ve sent an email to {formik.values.email} with password
                    reset instructions.
                  </AlertDescription>
                </Alert>
                <Text>
                  If the email doesn’t show up soon, check your spam folder. We
                  sent it from <strong>confirm@kula.ai</strong>.
                </Text>
                <Stack alignItems="center">
                  <Link
                    as={ReactLink}
                    to="/login"
                    color="primary-500"
                    fontWeight="semiBold"
                    fontSize="14px"
                    textDecoration="underline"
                    data-test-id="return-to-login-link"
                  >
                    Return to login
                  </Link>
                </Stack>
              </>
            ) : null}
            {!state ? (
              <Form>
                <Stack spacing={4}>
                  <Field name="email">
                    {({ field, form, meta }) => (
                      <FormControl
                        isInvalid={isInvalidField(form, meta)}
                        isRequired
                      >
                        <FormLabel
                          htmlFor={field.name}
                          fontSize="14px"
                          fontWeight="semiBold"
                        >
                          Email address
                        </FormLabel>
                        <Input
                          {...field}
                          id={field.name}
                          value={field.value || ''}
                          type="email"
                        />
                        <Text variant="muted" mt="12px">
                          We’ll email you a password reset link
                        </Text>
                        <FormErrorMessage
                          flexDir="column"
                          alignItems="flex-start"
                        >
                          {renderErrors('Email Address', meta.error)}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>
                <Stack py={6}>
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={formik.isSubmitting}
                    data-test-id="send-reset-instructions-button"
                  >
                    Send reset instructions
                  </Button>
                </Stack>
                <Flex alignItems="center">
                  <Link
                    flex={1}
                    as={ReactLink}
                    to="/login"
                    color="brand.100"
                    fontWeight="semiBold"
                    fontSize="14px"
                    textDecoration="underline"
                    data-test-id="return-to-login-link"
                  >
                    Return to Login
                  </Link>
                </Flex>
              </Form>
            ) : null}
          </Stack>
        )}
      </Formik>
    </Stack>
  );
}
